import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Globe, AlertCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import AIWritingAssistant from '@/components/organiser/AIWritingAssistant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrganiserManageEventPage() {
  const { eventId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [newEditionOpen, setNewEditionOpen] = useState(false);
  const [newEditionData, setNewEditionData] = useState({
    slug: '',
    dates: { start: '', end: '', display: '' }
  });
  
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    fetchEvent();
  }, [eventId, token]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/organiser/events/${eventId}?token=${token}`);
      setEvent(response.data.event);
      setHasPendingChanges(response.data.hasPendingChanges);
      setFormData({
        name: response.data.event.name,
        city: response.data.event.city,
        venueName: response.data.event.venueName,
        dates: response.data.event.dates,
        shortDescription: response.data.event.shortDescription,
        longDescription: response.data.event.longDescription,
        organiserWebsite: response.data.event.organiserWebsite || '',
        officialUrl: response.data.event.officialUrl || '',
        sectorTags: response.data.event.sectorTags || []
      });
    } catch (err) {
      console.error('Failed to fetch event:', err);
      if (err.response?.status === 401) {
        setError('This link has expired. Please contact us at enquiries@jigsawconferences.co.uk if you need to update your listing.');
      } else {
        setError('Failed to load event details. Please try again or contact support.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('dates.')) {
      const dateField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        dates: { ...prev.dates, [dateField]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.sectorTags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        sectorTags: [...prev.sectorTags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      sectorTags: prev.sectorTags.filter(t => t !== tag)
    }));
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await axios.post(`${API}/organiser/events/${eventId}/request-update?token=${token}`, formData);
      toast.success('Update submitted', {
        description: "Thank you – your changes have been sent to our team. We'll review and confirm once the listing has been updated."
      });
      setHasPendingChanges(true);
    } catch (err) {
      console.error('Failed to submit update:', err);
      toast.error('Failed to submit update', {
        description: err.response?.data?.detail || 'Please try again or contact support'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitNewEdition = async () => {
    try {
      setSubmitting(true);
      
      const newEdition = {
        ...formData,
        slug: newEditionData.slug,
        dates: newEditionData.dates,
        citySlug: event.citySlug,
        venueSlug: event.venueSlug,
        organiserName: event.organiserName,
        ownerType: 'organiser',
        ownerEmail: event.ownerEmail,
        seriesKey: event.seriesKey || event.slug.split('-').slice(0, -1).join('-'),
        status: 'upcoming'
      };
      
      await axios.post(`${API}/organiser/events/${eventId}/request-new-edition?token=${token}`, newEdition);
      
      toast.success('New edition submitted', {
        description: "We've received your details for the next edition of this event. Our team will review and publish a new page if approved."
      });
      setNewEditionOpen(false);
    } catch (err) {
      console.error('Failed to submit new edition:', err);
      toast.error('Failed to submit new edition', {
        description: err.response?.data?.detail || 'Please try again or contact support'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">Loading your event...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <CardTitle>Link Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-gray-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
              <Globe className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Your Event Listing</h1>
              <p className="text-sm text-gray-600">Jigsaw Conferences / InternationalVenues.com</p>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>You're managing:</strong> {event.name} – {event.venueName}, {event.city}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {hasPendingChanges && (
          <Alert className="mb-6 bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle className="text-amber-900">Changes Pending Review</AlertTitle>
            <AlertDescription className="text-amber-700">
              You have submitted changes that are awaiting review by our team. You can still make additional edits below.
            </AlertDescription>
          </Alert>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Event Details</CardTitle>
            <CardDescription>
              Use this page to request updates to your event listing or submit the next edition. Your changes won't go live immediately – our team will review and approve them first.
            </CardDescription>
          </CardHeader>
        </Card>

        <form onSubmit={handleSubmitUpdate} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Event Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Select value={formData.city} onValueChange={(value) => handleSelectChange('city', value)} required>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="London">London</SelectItem>
                      <SelectItem value="Birmingham">Birmingham</SelectItem>
                      <SelectItem value="Manchester">Manchester</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="venueName">Venue Name *</Label>
                  <Input
                    id="venueName"
                    name="venueName"
                    value={formData.venueName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dates */}
          <Card>
            <CardHeader>
              <CardTitle>Event Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="dates.start">Start Date *</Label>
                  <Input
                    type="date"
                    id="dates.start"
                    name="dates.start"
                    value={formData.dates?.start}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dates.end">End Date *</Label>
                  <Input
                    type="date"
                    id="dates.end"
                    name="dates.end"
                    value={formData.dates?.end}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="dates.display">Display String *</Label>
                  <Input
                    id="dates.display"
                    name="dates.display"
                    value={formData.dates?.display}
                    onChange={handleChange}
                    required
                    placeholder="11–12 November 2026"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Descriptions */}
          <Card>
            <CardHeader>
              <CardTitle>Event Description</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="shortDescription">Short Description *</Label>
                <Textarea
                  id="shortDescription"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  required
                  rows={2}
                  placeholder="1-2 sentence teaser"
                />
              </div>

              <div>
                <Label htmlFor="longDescription">Full Description *</Label>
                <Textarea
                  id="longDescription"
                  name="longDescription"
                  value={formData.longDescription}
                  onChange={handleChange}
                  required
                  rows={6}
                  placeholder="Focus on what the event offers for delegates and exhibitors"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Focus on what the event offers for delegates and exhibitors. We may lightly edit for clarity and house style.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* AI Writing Assistant */}
          <AIWritingAssistant
            eventId={eventId}
            token={token}
            currentDescription={formData.longDescription}
            onDescriptionGenerated={(desc) => {
              setFormData(prev => ({ ...prev, longDescription: desc }));
            }}
            onSocialGenerated={(posts) => {
              // Social posts are copied to clipboard, no form field needed
              console.log('Social posts generated:', posts);
            }}
          />

          {/* URLs & Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Links & Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="organiserWebsite">Organiser Website</Label>
                  <Input
                    type="url"
                    id="organiserWebsite"
                    name="organiserWebsite"
                    value={formData.organiserWebsite}
                    onChange={handleChange}
                    placeholder="https://"
                  />
                </div>

                <div>
                  <Label htmlFor="officialUrl">Official Event URL</Label>
                  <Input
                    type="url"
                    id="officialUrl"
                    name="officialUrl"
                    value={formData.officialUrl}
                    onChange={handleChange}
                    placeholder="https://"
                  />
                </div>
              </div>

              <div>
                <Label>Sector Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag and press Enter"
                  />
                  <Button type="button" onClick={handleAddTag} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.sectorTags?.map((tag, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-orange-600 hover:bg-orange-700 flex-1"
            >
              {submitting ? 'Submitting...' : 'Submit Update for Review'}
            </Button>

            <Dialog open={newEditionOpen} onOpenChange={setNewEditionOpen}>
              <DialogTrigger asChild>
                <Button type="button" variant="outline" className="flex-1">
                  Submit as New Edition
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Submit New Edition</DialogTitle>
                  <DialogDescription>
                    Use this when you're creating next year's edition or a new set of dates.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label htmlFor="new-slug">New Slug *</Label>
                    <Input
                      id="new-slug"
                      value={newEditionData.slug}
                      onChange={(e) => setNewEditionData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="event-name-2027"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-start">Start Date *</Label>
                    <Input
                      type="date"
                      id="new-start"
                      value={newEditionData.dates.start}
                      onChange={(e) => setNewEditionData(prev => ({
                        ...prev,
                        dates: { ...prev.dates, start: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-end">End Date *</Label>
                    <Input
                      type="date"
                      id="new-end"
                      value={newEditionData.dates.end}
                      onChange={(e) => setNewEditionData(prev => ({
                        ...prev,
                        dates: { ...prev.dates, end: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-display">Display String *</Label>
                    <Input
                      id="new-display"
                      value={newEditionData.dates.display}
                      onChange={(e) => setNewEditionData(prev => ({
                        ...prev,
                        dates: { ...prev.dates, display: e.target.value }
                      }))}
                      placeholder="11–12 November 2027"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewEditionOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleSubmitNewEdition}
                    disabled={submitting || !newEditionData.slug || !newEditionData.dates.start}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    {submitting ? 'Submitting...' : 'Submit New Edition'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </form>

        {/* Footer Info */}
        <Card className="mt-8 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <p className="text-sm text-blue-900 text-center">
              Questions? Contact us at <a href="mailto:enquiries@jigsawconferences.co.uk" className="font-medium underline">enquiries@jigsawconferences.co.uk</a>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
